import { FC, useEffect, useState } from 'react';

import { useTheme } from '@mui/material/styles';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { CommentResponse } from '@/api/data-contracts';
import AuthorChips from '@/features/comments/ui/Comments/AuthorChips';
import { getPlatformChipInfo } from '@/features/comments/ui/Comments/commentUtils';
import { getModuleAccentColor } from '@/shared/lib/hooks/useModuleChipColors';
import Avatar from '@/shared/ui/Avatar';

import { AuthorName, ContentBox, HeaderBox, Item, Message, TimeAgo } from './CommentItem.styles';
import { useCommentItem } from './useCommentItem';

dayjs.extend(relativeTime);

interface Props {
  comment: CommentResponse;
  highlighted?: boolean;
}

const useUserTypeColor = (userType: string | undefined): string => {
  const { palette } = useTheme();
  const platformChipInfo = getPlatformChipInfo(userType);
  return platformChipInfo
    ? getModuleAccentColor(palette, platformChipInfo.userType)
    : palette.accent.main;
};

const CommentItem: FC<Props> = ({ comment, highlighted = false }) => {
  const { authorName, userType, authorRole } = useCommentItem(comment);
  const avatarColor = useUserTypeColor(userType);
  const [isHighlighted, setIsHighlighted] = useState(highlighted);

  useEffect(() => {
    if (!highlighted) return;
    setIsHighlighted(true);
    const timer = setTimeout(() => setIsHighlighted(false), 3000);
    return () => clearTimeout(timer);
  }, [highlighted]);

  return (
    <Item
      $highlighted={isHighlighted}
      data-testid="comment"
      data-comment-id={comment.id}
      data-highlighted={highlighted}
    >
      <Avatar
        userId={comment.author?.id ?? null}
        name={authorName}
        sx={{
          width: '22px',
          height: '22px',
          backgroundColor: avatarColor,
          fontSize: '10px',
          fontWeight: 500,
          flexShrink: 0,
        }}
      />

      <ContentBox>
        <HeaderBox>
          <AuthorName>{authorName}</AuthorName>
          <AuthorChips platformChipInfo={getPlatformChipInfo(userType)} authorRole={authorRole} />
          <TimeAgo>{dayjs(comment.created).fromNow()}</TimeAgo>
        </HeaderBox>
        <Message>{comment.message}</Message>
      </ContentBox>
    </Item>
  );
};

export default CommentItem;
